import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import cloneDeep from 'lodash/cloneDeep';
import { dateToLocalDateString, dateToPeriodLabel } from './weeklyReports';
import {
    endOfWeekFromStart,
    extractHasMoreOlder,
    mergeById,
    normalizeDataset,
    oldestPeriodStartAmong,
    parsePaymentDate,
    startOfWeekContaining,
    stripTime,
} from './expenses';
import { priceOnFinish } from '../../supplementary_components/priceInputDelegate';
import { API_ROOT, sendRequest } from '../../supplementary_components/requests';
import { localDateToUSFormat } from '../../general_components/content_management/item_page/driver_page_components/payoutAdjustmentsSection';
import { addPopup, addNotification } from '../../supplementary_components/store';
import { Form } from '../../general_components/forms/Form';
import { LoadingIcon } from '../../supplementary_components/loading_icon';
import listStyles from '../../general_components/content_management/list_view/listView.module.scss';
import reportStyles from './reports.module.scss';
import { userHasAccess } from '../../supplementary_components/userDataDelegate';
import { ThreeDotsButton } from '../../general_components/content_management/reusableItems/buttons';
import { MdHistory } from 'react-icons/md';
import { openActivitiesPopup } from '../../general_components/activitiesPopup/activitiesPopup';
import activitiesStyles from '../../general_components/activitiesPopup/activitiesPopup.module.scss';
import {
    ATTACHMENT_ENTITY_TYPES,
    getAttachmentEntityConfig,
} from '../../general_components/attachmentsPopup/attachmentEntityConfig';
import { openAttachmentsPopup } from '../../general_components/attachmentsPopup/attachmentsPopup';
import { entityHasAttachments } from '../../general_components/attachmentsPopup/supplementaryFilesListing';
import reportListStyles from './payableInvoicesList.module.scss';
import { ListDescriptionCell } from '../../general_components/descriptionPopup/descriptionPopup';
import {
    TRUCK_FIELD,
    TRAILER_FIELD,
    buildInvoicePayload,
    buildPayableInvoiceAmountField,
    buildPayableInvoiceDateFields,
    buildPayableInvoicePaidByField,
    buildPayableInvoiceShopInfoField,
    formatPaidBy,
    equipmentTypeOnChange,
    validateInvoiceEquipment,
} from './payableInvoiceFormFields';

const INVOICE_GRID_COLUMNS = '0.5fr 0.8fr 0.95fr 0.8fr 0.8fr 1.2fr 1.2fr 0.8fr 1.8fr 15rem 10rem 3rem';
const PAYABLE_INVOICE_ACTIVITIES_POPUP_KIND = 'payableInvoiceActivities';
const PAYABLE_INVOICE_DESCRIPTION_POPUP_KIND = 'payableInvoiceDescription';
const PAYABLE_INVOICE_ATTACHMENTS_POPUP_KIND =
    getAttachmentEntityConfig(ATTACHMENT_ENTITY_TYPES.PAYABLE_INVOICE).popupKind;
const INVOICE_ACCESS_LEVELS = { key: 'PAYABLE_INVOICES', level: 'MANAGE' };

/** Matches {@code InvoiceStatus} in the backend. */
const PAYABLE_INVOICE_STATUSES = ['Open', 'PartiallyPaid', 'Paid', 'Overdue', 'Void'];

const PAYABLE_INVOICE_STATUS_OPTIONS = PAYABLE_INVOICE_STATUSES.map((status) => ({
    value: status,
    label: formatInvoiceStatus(status),
}));

/** Same pattern as listView RowItem — injects CSS for child text. */
const FormattedCell = styled.div`
    ${(props) => props.$conditionalFormatting || ''}
`;

function buildDisplayColumns({ userCanEdit, onFilesUpdated }) {
    return [
        {
            label: 'Invoice #',
            columnName: 'invoiceNumber',
            valueInterpreter: (val) => (val != null ? String(val) : '-'),
        },
        {
            label: 'Status',
            columnName: 'status',
            valueInterpreter: (val) => formatInvoiceStatus(val),
            conditionalFormatting: (status) => invoiceStatusConditionalFormatting(status),
        },
        {
            label: 'Paid By',
            columnName: 'paidBy',
            valueInterpreter: (val) => formatPaidBy(val),
        },
        {
            label: 'Created',
            columnName: 'createdDate',
            valueInterpreter: (val) => (val ? localDateToUSFormat(val) : '-'),
        },
        {
            label: 'Due',
            columnName: 'dueDate',
            valueInterpreter: (val) => (val ? localDateToUSFormat(val) : '-'),
        },
        {
            label: 'Equipment',
            columnName: 'equipment',
            valueInterpreter: (_, row) => formatEquipment(row),
        },
        {
            label: 'Shop',
            columnName: 'shopName',
            valueInterpreter: (val) => (val ? String(val) : '-'),
        },
        {
            label: 'Amount',
            columnName: 'amount',
            valueInterpreter: (val) => priceOnFinish(val),
        },
        {
            label: 'Description',
            columnName: 'description',
            render: (row) => (
                <ListDescriptionCell
                    description={row.description}
                    titleLabel={row.invoiceNumber != null ? String(row.invoiceNumber) : ''}
                    titlePrefix="Description — Invoice"
                    popupKind={PAYABLE_INVOICE_DESCRIPTION_POPUP_KIND}
                />
            ),
        },
        {
            label: 'Paperwork',
            columnName: 'paperwork',
            render: (row) => (
                <PaperworkCell
                    invoice={row}
                    canEdit={userCanEdit}
                    onFilesUpdated={onFilesUpdated}
                />
            ),
        },
        {
            label: 'Latest Actions',
            columnName: 'latestActions',
            render: (row) => (
                <div
                    className={activitiesStyles.latestActionsContainer}
                    onClick={(e) => {
                        e.stopPropagation();
                        openActivitiesPopup({
                            targetType: 'PAYABLE_INVOICE',
                            objectId: row.id,
                            titleLabel: row.invoiceNumber != null ? String(row.invoiceNumber) : '',
                            popupKind: PAYABLE_INVOICE_ACTIVITIES_POPUP_KIND,
                        });
                    }}
                >
                    <MdHistory />
                </div>
            ),
        },
    ];
}

export function readOnlyTruckInvoiceColumns() {
    return buildDisplayColumns({ userCanEdit: false, onFilesUpdated: () => {} })
        .filter((col) => col.columnName !== 'equipment');
}

function formatInvoiceStatus(status) {
    if (!status) return '-';
    return String(status).replace(/([a-z])([A-Z])/g, '$1 $2');
}

function invoiceStatusConditionalFormatting(status) {
    switch (status) {
        case 'Overdue':
            return '& > span { color: var(--lightAlert); font-weight: bold; }';
            case 'Open':
            case 'PartiallyPaid':
            return '& > span { color: var(--inProgressYellow); font-weight: bold; }';
        case 'Paid':
            return '& > span { color: var(--main-color); font-weight: bold; }';
        case 'Void':
            return '& > span { color: var(--light-gray); font-weight: bold; }';
        default:
            return null;
    }
}

export function renderInvoiceColumnCell(col, invoice) {
    if (col.render) {
        return col.render(invoice);
    }
    const raw = col.columnName === 'equipment' ? invoice : invoice[col.columnName];
    const text = col.valueInterpreter ? col.valueInterpreter(raw, invoice) : raw;
    const formatting =
        typeof col.conditionalFormatting === 'function' ? col.conditionalFormatting(raw) : null;
    const content = <span>{text}</span>;
    if (formatting) {
        return <FormattedCell $conditionalFormatting={formatting}>{content}</FormattedCell>;
    }
    return content;
}

function formatEquipment(invoice) {
    const type = invoice?.equipmentType;
    if (type === 'Truck' && invoice?.truck?.truckNumber) {
        return `Truck · ${invoice.truck.truckNumber}`;
    }
    if (type === 'Trailer' && invoice?.trailer?.trailerNumber) {
        return `Trailer · ${invoice.trailer.trailerNumber}`;
    }
    return type || '-';
}

function PaperworkCell({ invoice, canEdit, onFilesUpdated }) {
    const uploaded = entityHasAttachments(invoice?.files);

    const openPopup = (e) => {
        e.stopPropagation();
        openAttachmentsPopup({
            entityType: ATTACHMENT_ENTITY_TYPES.PAYABLE_INVOICE,
            entity: invoice,
            titleLabel: invoice?.invoiceNumber != null ? String(invoice.invoiceNumber) : '',
            canEdit,
            onEntityUpdated: onFilesUpdated,
            popupKind: PAYABLE_INVOICE_ATTACHMENTS_POPUP_KIND,
        });
    };

    if (!uploaded) {
        return (
            <button
                type="button"
                className={reportListStyles.paperworkMissingButton}
                onClick={openPopup}
            >
                Missing
            </button>
        );
    }

    return (
        <button type="button" className={reportListStyles.seeAttachmentsButton} onClick={openPopup}>
            See attachments
        </button>
    );
}

function groupInvoicesByWeek(invoices, firstDayOfAWeek) {
    const buckets = new Map();
    for (const invoice of invoices) {
        let weekStart;
        let weekEnd;
        if (invoice.period?.startDate && invoice.period?.endDate) {
            weekStart = parsePaymentDate(invoice.period.startDate);
            weekEnd = parsePaymentDate(invoice.period.endDate);
        } else {
            const created = parsePaymentDate(invoice.createdDate);
            if (!created) continue;
            weekStart = startOfWeekContaining(created, firstDayOfAWeek);
            weekEnd = endOfWeekFromStart(weekStart);
        }
        if (!weekStart) continue;
        const key = dateToLocalDateString(weekStart);
        if (!buckets.has(key)) {
            buckets.set(key, {
                weekStart,
                weekEnd,
                invoices: [],
            });
        }
        buckets.get(key).invoices.push(invoice);
    }
    const groups = [...buckets.values()].sort((a, b) => b.weekStart.getTime() - a.weekStart.getTime());
    for (const g of groups) {
        g.invoices.sort((a, b) => {
            const byDate = String(b.createdDate || '').localeCompare(String(a.createdDate || ''));
            if (byDate !== 0) return byDate;
            return String(b.invoiceNumber ?? '').localeCompare(String(a.invoiceNumber ?? ''));
        });
    }
    return groups;
}

function extractInvoiceFromApiSuccess(res) {
    if (res == null || typeof res !== 'object') return null;
    if (res.id != null && (res.invoiceNumber !== undefined || res.amount !== undefined)) return res;
    if (res.dataset != null) {
        const d = Array.isArray(res.dataset) ? res.dataset[0] : res.dataset;
        if (d && typeof d === 'object' && d.id != null) return d;
    }
    if (res.data != null && typeof res.data === 'object' && res.data.id != null) return res.data;
    return null;
}

function mergeInvoiceIntoList(prev, invoice) {
    if (!invoice || invoice.id == null) return prev;
    const idStr = String(invoice.id);
    const idx = prev.findIndex((row) => String(row.id) === idStr);
    if (idx >= 0) {
        const next = [...prev];
        const merged = { ...next[idx], ...invoice };
        if ((invoice.files == null || invoice.files === undefined) && next[idx].files != null) {
            merged.files = next[idx].files;
        }
        next[idx] = merged;
        return next;
    }
    return [invoice, ...prev];
}

function buildInvoiceAddRequestBody(data, file) {
    const body = new FormData();
    body.append('invoiceData', JSON.stringify(buildInvoicePayload(data)));
    if (file == null) {
        return body;
    }
    body.append('file', file);
    body.append('fileName', file.name);
    body.append('fileType', 'any');
    return body;
}

function uploadInvoiceFilesSequentially(invoiceId, files, onEachSuccess, onDone, onError) {
    if (!files?.length) {
        onDone?.(null);
        return;
    }
    const uploadAt = (index, lastRes) => {
        if (index >= files.length) {
            onDone?.(lastRes);
            return;
        }
        const body = new FormData();
        body.append('file', files[index]);
        body.append('fileName', files[index].name);
        body.append('fileType', 'any');
        body.append('invoiceId', invoiceId);
        sendRequest(
            'POST',
            API_ROOT + 'invoices/addFile',
            null,
            (res) => {
                onEachSuccess?.(res);
                uploadAt(index + 1, res);
            },
            () => {
                onError?.(index, lastRes);
            },
            body,
            {},
            (b) => b,
            false,
        );
    };
    uploadAt(0, null);
}

function buildInvoiceForm(invoice) {
    const isEdit = !!invoice;
    const showTruckInitially = isEdit && invoice?.equipmentType === 'Truck';
    const showTrailerInitially = isEdit && invoice?.equipmentType === 'Trailer';
    const [createdDateField, dueDateField] = buildPayableInvoiceDateFields({ invoice: isEdit ? invoice : null });

    const conf = {
        formAccessLevel: INVOICE_ACCESS_LEVELS,
        formTitle: isEdit ? 'Edit Invoice' : 'Add Invoice',
        customSubmitButton: isEdit ? 'Save Invoice' : 'Add Invoice',
        name: isEdit ? 'editPayableInvoiceForm' : 'addPayableInvoiceForm',
        customMethod: isEdit ? 'POST' : 'POST',
        addLink: isEdit ? API_ROOT + `invoices/${invoice.id}` : API_ROOT + 'invoices',
        successMessage: isEdit ? 'Invoice was updated' : 'Invoice was added',
        EZCloseAllowed: false,
        _pendingAttachmentFiles: [],
        ...(isEdit
            ? { buildRequestBody: (data) => buildInvoicePayload(data) }
            : {
                  buildRequestBody: (data, file, files) => {
                      const all = Array.isArray(files) && files.length
                          ? files
                          : (file ? [file] : []);
                      conf._pendingAttachmentFiles = all.slice(1);
                      return buildInvoiceAddRequestBody(data, all[0] || null);
                  },
                  // Omit Content-Type so the browser sets multipart boundary; companyId is injected at request time.
                  customContentType: {},
                  preSendManipulation: (body) => body,
              }),
        customCheckBeforeSending: validateInvoiceEquipment,
        columns: [
            isEdit
                ? {
                      hiddenField: true,
                      name: 'id',
                      defaultValue: () => invoice.id,
                  }
                : null,
            isEdit
                ? {
                      label: 'Invoice #',
                      name: 'invoiceNumberDisplay',
                      defaultValue: () => String(invoice.invoiceNumber ?? ''),
                      disabled: true,
                      ignoreAsRegularField: true,
                  }
                : null,
            isEdit
                ? {
                      label: 'Status',
                      name: 'status',
                      type: 'select',
                      dataset: PAYABLE_INVOICE_STATUS_OPTIONS,
                      mandatory: true,
                      defaultValue: () => invoice?.status ?? 'Open',
                  }
                : null,
            {
                label: 'Equipment type',
                name: 'equipmentType',
                type: 'select',
                dataset: ['', 'Truck', 'Trailer'],
                mandatory: true,
                defaultValue: () => (isEdit ? invoice?.equipmentType ?? '' : ''),
                onChange: equipmentTypeOnChange,
            },
            {
                ...TRUCK_FIELD,
                mandatory: true,
                customDisplayMode: showTruckInitially ? 'block' : 'none',
                disabled: !showTruckInitially,
                defaultValue: () => invoice?.truck?.truckNumber ?? '',
                predefinedDatValue: invoice?.truck
                    ? JSON.stringify({ id: invoice.truck.id, truckNumber: invoice.truck.truckNumber })
                    : null,
            },
            {
                ...TRAILER_FIELD,
                mandatory: true,
                customDisplayMode: showTrailerInitially ? 'block' : 'none',
                disabled: !showTrailerInitially,
                defaultValue: () => invoice?.trailer?.trailerNumber ?? '',
                predefinedDatValue: invoice?.trailer
                    ? JSON.stringify({
                          id: invoice.trailer.id,
                          trailerNumber: invoice.trailer.trailerNumber,
                      })
                    : null,
            },
            buildPayableInvoiceAmountField({ invoice: isEdit ? invoice : null }),
            createdDateField,
            buildPayableInvoiceShopInfoField({ invoice: isEdit ? invoice : null }),
            dueDateField,
            buildPayableInvoicePaidByField({ invoice: isEdit ? invoice : null }),
            {
                label: 'Description',
                name: 'description',
                type: 'textArea',
                rowCount: 4,
                defaultValue: () => invoice?.description ?? '',
            },
            !isEdit
                ? {
                      label: 'Paperwork',
                      name: 'paperwork',
                      type: 'dropBox',
                      fileType: 'any',
                      multiple: true,
                      file: null,
                      files: [],
                      ignoreAsRegularField: true,
                  }
                : null,
        ].filter(Boolean),
    };
    return conf;
}

function fetchInvoicesResponse(queryString, signal, invoiceId = null) {
    const idParam =
        invoiceId != null && String(invoiceId).trim() !== ''
            ? `&id=${encodeURIComponent(String(invoiceId))}`
            : '';
    const qs = (queryString || '?pageNumber=0') + idParam;
    return new Promise((resolve) => {
        sendRequest(
            'GET',
            `${API_ROOT}invoices${qs}`,
            signal,
            (res) => resolve(res ?? {}),
            () => resolve({ dataset: [], hasMoreOlder: false }),
            null,
        );
    });
}

export const PayableInvoicesManagement = ({ companyData, elementIdentifier }) => {
    const firstDayOfAWeek = companyData?.firstDayOfAWeek || 'monday';
    const singleInvoiceMode =
        elementIdentifier != null && String(elementIdentifier).trim() !== '';

    const [allInvoices, setAllInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadMoreBusy, setLoadMoreBusy] = useState(false);
    const [error, setError] = useState(null);
    const [hasMoreOlder, setHasMoreOlder] = useState(true);
    const [oldestLoadedPeriodStart, setOldestLoadedPeriodStart] = useState(null);

    const userCanEdit = userHasAccess(INVOICE_ACCESS_LEVELS);

    const applyInvoiceFromApi = useCallback((apiValue) => {
        const row = extractInvoiceFromApiSuccess(apiValue);
        if (row) {
            setAllInvoices((prev) => mergeInvoiceIntoList(prev, row));
        } else {
            addNotification({
                message: 'Invoice was saved but the response could not be merged into the list.',
                type: 'alert',
            });
        }
    }, []);

    const displayColumns = useMemo(
        () => buildDisplayColumns({ userCanEdit, onFilesUpdated: applyInvoiceFromApi }),
        [userCanEdit, applyInvoiceFromApi],
    );

    const openAddInvoice = useCallback(() => {
        const formConf = buildInvoiceForm();
        addPopup({
            content: Form,
            popupData: { configuration: formConf },
            postSuccessAction: (value) => {
                applyInvoiceFromApi(value);
                const invoice = extractInvoiceFromApiSuccess(value);
                const pending = formConf._pendingAttachmentFiles || [];
                if (!invoice?.id || !pending.length) {
                    return;
                }
                uploadInvoiceFilesSequentially(
                    invoice.id,
                    pending,
                    (res) => applyInvoiceFromApi(res),
                    null,
                    (failedIndex) => {
                        addNotification({
                            message: `Invoice created, but only ${failedIndex + 1} of ${pending.length + 1} attachments uploaded`,
                            type: 'alert',
                        });
                    },
                );
            },
            EZCloseAllowed: false,
        });
    }, [applyInvoiceFromApi]);

    const openEditInvoice = useCallback(
        (invoice) => {
            const formConf = cloneDeep(buildInvoiceForm(invoice));
            addPopup({
                content: Form,
                popupData: { configuration: formConf },
                postSuccessAction: (value) => applyInvoiceFromApi(value),
                EZCloseAllowed: false,
            });
        },
        [applyInvoiceFromApi],
    );

    useEffect(() => {
        let cancelled = false;
        const ac = new AbortController();

        const run = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = singleInvoiceMode
                    ? await fetchInvoicesResponse('?pageNumber=0', ac.signal, elementIdentifier)
                    : await fetchInvoicesResponse('?pageNumber=0', ac.signal);
                if (cancelled) return;
                const rows = normalizeDataset(res);
                setAllInvoices(rows);
                if (!singleInvoiceMode) {
                    setOldestLoadedPeriodStart(oldestPeriodStartAmong(rows));
                    setHasMoreOlder(extractHasMoreOlder(res));
                }
            } catch (e) {
                if (!cancelled) setError(e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        run();
        return () => {
            cancelled = true;
            ac.abort();
        };
    }, [elementIdentifier, singleInvoiceMode]);

    const weekGroups = useMemo(
        () => (singleInvoiceMode ? [] : groupInvoicesByWeek(allInvoices, firstDayOfAWeek)),
        [allInvoices, firstDayOfAWeek, singleInvoiceMode],
    );

    const onLoadMore = useCallback(async () => {
        if (singleInvoiceMode || !hasMoreOlder || loadMoreBusy || !oldestLoadedPeriodStart || loading) return;
        setLoadMoreBusy(true);
        const ac = new AbortController();
        const qs = `?beforePeriodStart=${dateToLocalDateString(
            stripTime(oldestLoadedPeriodStart),
        )}&pageNumber=0`;
        try {
            const res = await fetchInvoicesResponse(qs, ac.signal);
            const rows = normalizeDataset(res);
            setHasMoreOlder(extractHasMoreOlder(res));
            setAllInvoices((prev) => {
                const { merged } = mergeById(prev, rows);
                const nextOldest = oldestPeriodStartAmong(merged);
                if (nextOldest) {
                    setOldestLoadedPeriodStart(nextOldest);
                }
                return merged;
            });
        } finally {
            setLoadMoreBusy(false);
        }
    }, [singleInvoiceMode, hasMoreOlder, loadMoreBusy, oldestLoadedPeriodStart, loading]);

    if (error) {
        return <div className="errorMessage">{error.customError ? error.customError : 'Something went wrong'}</div>;
    }

    if (loading) {
        return <LoadingIcon state={true} height="10rem" />;
    }

    return (
        <div className={reportStyles.mainContainer}>
            <div className={listStyles.pageHeader}>
                <div>{singleInvoiceMode ? 'Maintenance Invoice' : 'Maintenance Invoices'}</div>
                {!singleInvoiceMode ? (
                    <div className={listStyles.actionsContainer}>
                        <div>
                            {userCanEdit ? (
                                <div className={`${listStyles.addButton} non-selectable`} onClick={openAddInvoice}>
                                    Add Invoice +
                                </div>
                            ) : null}
                        </div>
                    </div>
                ) : null}
            </div>

            <div
                className={listStyles.container}
                // eslint-disable-next-line
                style={{ ['--list-columns']: INVOICE_GRID_COLUMNS }}
            >
                <div className={listStyles.listViewHeaderWrapper} style={{ gridTemplateColumns: 'var(--list-columns)' }}>
                    {displayColumns.map((col) => (
                        <div key={col.columnName} className={listStyles.listHeaderLabel}>
                            <span className={listStyles.labelText}>{col.label}</span>
                        </div>
                    ))}
                    <div className={listStyles.listHeaderLabel} />
                </div>

                {singleInvoiceMode ? (
                    allInvoices.length === 0 ? (
                        <div style={{ display: 'grid', gridTemplateColumns: 'var(--list-columns)', width: '100%' }}>
                            <div className={reportStyles.expensesEmptyBanner} style={{ gridColumn: '1 / -1' }}>
                                Payable invoice not found.
                            </div>
                        </div>
                    ) : (
                        allInvoices.map((invoice) => (
                            <div key={invoice.id ?? invoice.invoiceNumber} className={listStyles.row}>
                                {displayColumns.map((col) => (
                                    <div
                                        key={col.columnName}
                                        style={
                                            col.columnName === 'description'
                                                ? { minWidth: 0, overflow: 'hidden' }
                                                : undefined
                                        }
                                    >
                                        {renderInvoiceColumnCell(col, invoice)}
                                    </div>
                                ))}
                                <ThreeDotsButton
                                    editOnly
                                    disabled={!userCanEdit}
                                    position="relative"
                                    onEdit={() => openEditInvoice(invoice)}
                                />
                            </div>
                        ))
                    )
                ) : weekGroups.length === 0 ? (
                    <div style={{ display: 'grid', gridTemplateColumns: 'var(--list-columns)', width: '100%' }}>
                        <div className={reportStyles.expensesEmptyBanner} style={{ gridColumn: '1 / -1' }}>
                            No payable invoices yet. Use “Load more” if you expect older records.
                        </div>
                    </div>
                ) : (
                    weekGroups.map((group) => (
                        <React.Fragment key={dateToLocalDateString(group.weekStart)}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'var(--list-columns)', width: '100%' }}>
                                <div className={reportStyles.expensesWeekDividerInner} style={{ gridColumn: '1 / -1' }}>
                                    {dateToPeriodLabel(group.weekStart, false, 'weekly', group.weekEnd)}
                                </div>
                            </div>
                            {group.invoices.map((invoice) => (
                                <div key={invoice.id ?? invoice.invoiceNumber} className={listStyles.row}>
                                    {displayColumns.map((col) => (
                                        <div
                                            key={col.columnName}
                                            style={
                                                col.columnName === 'description'
                                                    ? { minWidth: 0, overflow: 'hidden' }
                                                    : undefined
                                            }
                                        >
                                            {renderInvoiceColumnCell(col, invoice)}
                                        </div>
                                    ))}
                                    <ThreeDotsButton
                                        editOnly
                                        disabled={!userCanEdit}
                                        position="relative"
                                        onEdit={() => openEditInvoice(invoice)}
                                    />
                                </div>
                            ))}
                        </React.Fragment>
                    ))
                )}
            </div>

            {!singleInvoiceMode && hasMoreOlder ? (
                <div className={listStyles.loadMoreButtonWrapper}>
                    <div
                        className={loadMoreBusy ? 'loading' : ''}
                        onClick={() => {
                            if (!loadMoreBusy) onLoadMore();
                        }}
                    >
                        {loadMoreBusy ? 'Loading…' : 'Load more'}
                    </div>
                </div>
            ) : !singleInvoiceMode ? (
                <div className={listStyles.loadMoreButtonWrapper}>
                    <div className={listStyles.noMoreToload}>No older invoices to load</div>
                </div>
            ) : null}
        </div>
    );
};
